import React, { useCallback, useEffect, useRef, useState } from "react";

export interface PdfViewerPageInputProps {
  /** 0-based index of the page currently shown in the viewer. */
  currentPage: number;
  totalPages: number;
  onJumpToPage: (zeroBasedPageIndex: number) => void;
}

export const PdfViewerPageInput: React.FC<PdfViewerPageInputProps> = ({
  currentPage,
  totalPages,
  onJumpToPage,
}) => {
  const [pageInputDraft, setPageInputDraft] = useState(() => String(currentPage + 1));
  const pageInputFocusedRef = useRef(false);
  const skipPageCommitAfterBlurRef = useRef(false);

  useEffect(() => {
    if (!pageInputFocusedRef.current) {
      setPageInputDraft(String(currentPage + 1));
    }
  }, [currentPage, totalPages]);

  const commitPageInput = useCallback(() => {
    const trimmed = pageInputDraft.trim();
    if (!/^\d+$/.test(trimmed)) {
      setPageInputDraft(String(currentPage + 1));
      return;
    }
    const n = Number(trimmed);
    if (n < 1 || n > totalPages) {
      setPageInputDraft(String(currentPage + 1));
      return;
    }
    const zeroBased = n - 1;
    if (zeroBased !== currentPage) {
      onJumpToPage(zeroBased);
    } else {
      setPageInputDraft(String(n));
    }
  }, [pageInputDraft, currentPage, totalPages, onJumpToPage]);

  return (
    <span className="chrome-pdf-page-info">
      <input
        type="text"
        inputMode="numeric"
        className="chrome-pdf-page-input"
        aria-label="Page number"
        title="Current page"
        maxLength={6}
        value={pageInputDraft}
        onChange={(e) => setPageInputDraft(e.target.value)}
        onFocus={() => {
          pageInputFocusedRef.current = true;
        }}
        onBlur={() => {
          pageInputFocusedRef.current = false;
          if (skipPageCommitAfterBlurRef.current) {
            skipPageCommitAfterBlurRef.current = false;
            return;
          }
          commitPageInput();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
            return;
          }
          if (e.key === "Escape") {
            e.preventDefault();
            skipPageCommitAfterBlurRef.current = true;
            setPageInputDraft(String(currentPage + 1));
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      <span className="chrome-pdf-page-info-suffix"> / {totalPages}</span>
    </span>
  );
};
